/** API fetch with Bearer token attachment. Per D-07, DATA-LAYER.md Section 2. */

import { getAccessToken } from "./auth";

// D-07: apiBaseUrl defaults to '' (NOT '/api'). In dev, Vite proxy handles /api/*.
// In prod, set VITE_API_BASE_URL=https://api.terum.ai per D-23.
let apiBaseUrl = "";

/** Configure the API base URL. Called once in main.tsx. */
export function configureApi({ baseUrl }: { baseUrl: string }) {
  apiBaseUrl = baseUrl;
}

/**
 * Structured API error. Preserves the HTTP status and the parsed JSON error body so
 * callers can branch on a server-provided `code` (e.g. 'extension_required') instead of
 * string-matching a message. `.message` keeps the legacy "API error: <status> <text>"
 * format so existing `err.message` rendering is unchanged.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly body?: unknown;
  constructor(status: number, statusText: string, body?: unknown) {
    super(`API error: ${status} ${statusText}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.code =
      body && typeof body === "object" && "code" in body && typeof (body as { code: unknown }).code === "string"
        ? (body as { code: string }).code
        : undefined;
  }
}

/** True when an error is an ApiError carrying a 404 — a missing/deleted resource. */
export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/**
 * True when an error is an ApiError carrying a 401/403 — an expired or rejected session.
 *
 * apiFetch deliberately does NOT auto-logout on this (sign-out stays owned by Supabase's
 * SIGNED_OUT event), so a stale token used to surface as the literal string
 * "API error: 401 Unauthorized" in a dead error panel with no way forward. Surfaces branch on
 * this to offer re-authentication instead.
 */
export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

/** Best-effort parse of an error response body as JSON. Never throws. */
async function readErrorBody(res: Response): Promise<unknown> {
  try {
    const text = await res.text();
    return text ? JSON.parse(text) : undefined;
  } catch {
    return undefined;
  }
}

/** Fetch from the API with Bearer token. Path must start with /api/. */
export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getAccessToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...init?.headers,
  };

  const res = await fetch(`${apiBaseUrl}${path}`, { ...init, headers });
  if (!res.ok) throw new ApiError(res.status, res.statusText, await readErrorBody(res));
  if (res.status === 204 || res.headers.get("content-length") === "0") return undefined as T;
  return res.json();
}

/**
 * Send FormData (file uploads) with Bearer token. Browser sets multipart boundary.
 *
 * XHR-backed, not fetch: fetch exposes NO upload progress, and the Ask composer's attachment
 * chip renders the standard determinate loading circle filled by real transport progress
 * (Teddy directive 2026-08-12). `onProgress` receives a 0..1 fraction whenever the browser
 * reports one; a server that sends no Content-Length simply never calls it (the chip falls
 * back to its indeterminate ring). Error semantics match apiFetch: non-2xx throws ApiError
 * with the parsed JSON body; a network failure rejects with a plain Error.
 */
export async function apiUpload<T>(path: string, body: FormData, onProgress?: (fraction: number) => void): Promise<T> {
  const token = await getAccessToken();
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${apiBaseUrl}${path}`);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) onProgress(Math.min(1, e.loaded / e.total));
      };
    }
    xhr.onload = () => {
      let parsed: unknown;
      try {
        parsed = xhr.responseText ? JSON.parse(xhr.responseText) : undefined;
      } catch {
        parsed = undefined;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new ApiError(xhr.status, xhr.statusText, parsed));
        return;
      }
      resolve(parsed as T);
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.onabort = () => reject(new Error("Upload aborted"));
    xhr.send(body);
  });
}

/** Streaming fetch — returns the raw Response for SSE/stream consumption. */
export async function apiStreamFetch(path: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...init?.headers,
  };

  const res = await fetch(`${apiBaseUrl}${path}`, { ...init, headers });
  if (!res.ok) throw new ApiError(res.status, res.statusText, await readErrorBody(res));
  return res;
}
